import classnames from 'classnames'
import * as React from 'react'
import * as Reakit from 'reakit/Popover'
import { Portal } from 'reakit/Portal'

import { useWindowSize } from '../../hooks/use-window-size'
import { Box, BoxProps, BoxTheme } from '../box'
import { getExtendedDialogState, useZIndex } from '../dialog'
import { TooltipArrow } from '../tooltip/tooltip-arrow'
import { PopoverStateReturn } from './hooks'

const POPOVER_HEIGHT_LIMIT_STYLE = {
  maxHeight: 'calc(var(--popover-max-height) - env(safe-area-inset-bottom))',
}

export interface PopoverProps extends BoxProps, Pick<Reakit.PopoverProps, 'hide'> {
  /**
   * The returned object from `usePopoverState`
   */
  state: PopoverStateReturn

  /**
   * Focus the popover when it opens
   *
   * @default true
   */
  focusOnShow?: Reakit.PopoverProps['unstable_autoFocusOnShow']

  /**
   * Focus the anchor element when it closes
   *
   * @default true
   */
  focusOnHide?: Reakit.PopoverProps['unstable_autoFocusOnHide']

  /**
   * Focus the anchor element when it closes
   *
   * @default undefined
   */
  finalFocusRef?: Reakit.PopoverProps['unstable_finalFocusRef']

  /**
   * Hides when clicked outside of popover, works with changing focus from the trigger component to another one
   *
   * @default true
   */
  hideOnClickOutside?: Reakit.PopoverProps['hideOnClickOutside']

  /**
   * Overlay click callback or if false is provided, overlay click calls hide func on popover state object
   *
   * @default undefined
   */
  onOverlayClick?: (() => void) | false

  /**
   * Popover wrapper css style object
   *
   * @default undefined
   */
  popoverWrapperStyle?: React.CSSProperties

  /**
   * Show popover arrow
   *
   * @default false
   */
  hasArrow?: boolean

  /**
   * Enable or disable backdrop click events
   *
   * @default true
   */
  hasBackdrop?: boolean

  /**
   * Set the popover to height to be limited by the viewport instead of having full height
   *
   * @default false
   */
  hasViewportHeightLimit?: boolean

  /**
   * Set the popover max-width to be limited by the trigger button
   */
  hasButtonWidthLimit?: boolean

  /**
   * Can be used together with `hasViewportHeightLimit` to limit the height of the popover
   *
   * @default undefined
   */
  popoverMaxHeight?: number
}

export function Popover({
  state,
  children,
  focusOnShow = true,
  focusOnHide = true,
  finalFocusRef,
  'aria-label': ariaLabel,
  hide,
  hideOnClickOutside = true,
  hasArrow = false,
  hasBackdrop = true,
  onOverlayClick,
  popoverWrapperStyle,
  popoverMaxHeight = Number.MAX_SAFE_INTEGER,
  theme = 'light',
  hasViewportHeightLimit = false,
  hasButtonWidthLimit = false,
  ...props
}: PopoverProps) {
  const { innerHeight } = useWindowSize()
  const zIndex = useZIndex()
  const { isFullyClosed } = getExtendedDialogState(state)

  const buttonElement = state.unstable_referenceRef.current
  React.useEffect(() => {
    if (!buttonElement || !state.visible || !(hasViewportHeightLimit || hasButtonWidthLimit)) return

    const buttonRect = buttonElement.getBoundingClientRect()
    const popoverId = buttonElement.getAttribute('aria-controls')
    const popoverWrapper = popoverId ? document.getElementById(popoverId) : null

    // Limit popover max-width to be width of the trigger button
    if (hasButtonWidthLimit) {
      popoverWrapper?.style.setProperty('max-width', `${buttonRect.width}px`)
    }

    // Limit popover height to be always be in viewport if `hasViewportHeightLimit` set
    if (hasViewportHeightLimit) {
      const [, offsetY] = state.offsets

      let elementMaxHeight = 'auto'
      if (state.placement.startsWith('bottom')) {
        const height = innerHeight - buttonRect.bottom - offsetY * 2
        elementMaxHeight = `${Math.min(height, popoverMaxHeight)}px`
      } else {
        const height = buttonRect.top - offsetY * 2
        elementMaxHeight = `${Math.min(height, popoverMaxHeight)}px`
      }

      popoverWrapper?.style.setProperty('--popover-max-height', `${elementMaxHeight}`)
    }
  }, [
    state.visible,
    state.offsets,
    buttonElement,
    hasViewportHeightLimit,
    hasButtonWidthLimit,
    innerHeight,
    state.placement,
    popoverMaxHeight,
  ])

  return (
    <Portal>
      <Reakit.Popover
        {...state}
        hide={hide ?? state.hide}
        hideOnClickOutside={hideOnClickOutside}
        unstable_autoFocusOnShow={focusOnShow}
        aria-label={ariaLabel}
        unstable_autoFocusOnHide={focusOnHide}
        unstable_finalFocusRef={finalFocusRef}
      >
        {({ onTransitionEnd, onAnimationEnd, ...innerProps }) => {
          if (isFullyClosed) return null

          return (
            <>
              <div
                className={classnames(zIndex, 'fixed inset-0', {
                  'pointer-events-none': !hasBackdrop,
                })}
                onClick={onOverlayClick === false ? hide ?? state.hide : onOverlayClick}
                onMouseDown={(event) => {
                  // prevents from page scroll when clicked outside of popover
                  event.preventDefault()
                }}
              />
              <div
                {...innerProps}
                className={classnames(zIndex, 'finviz-popover pointer-events-none outline-none')}
                style={{ ...innerProps.style, ...popoverWrapperStyle }}
              >
                <Box
                  onTransitionEnd={onTransitionEnd}
                  onAnimationEnd={onAnimationEnd}
                  data-placement={state.placement}
                  theme={theme}
                  {...props}
                  className={classnames('pointer-events-auto', props.className)}
                  style={hasViewportHeightLimit ? { ...POPOVER_HEIGHT_LIMIT_STYLE, ...props.style } : props.style}
                >
                  {hasArrow && (
                    <Reakit.PopoverArrow {...state} size={16}>
                      <TooltipArrow
                        placement={state.placement}
                        className={classnames({
                          'fill-white stroke-gray-100 dark:fill-gray-800 dark:stroke-gray-600':
                            BoxTheme[theme] === BoxTheme.light,
                          'fill-gray-800 stroke-gray-600': BoxTheme[theme] === BoxTheme.dark,
                        })}
                      />
                    </Reakit.PopoverArrow>
                  )}
                  {children}
                </Box>
              </div>
            </>
          )
        }}
      </Reakit.Popover>
    </Portal>
  )
}
